<template>
  <div class="collection-calendar-main">
    <!-- 地区一覧画面 -->
    <CollectionCalendarView
      v-if="view === 'list'"
      :districts="districts"
      :loading="loading"
      :error="error"
      @open-calendar="openCalendar"
      @back-to-menu="handleBackToMenu"
    />

    <!-- カレンダー詳細モーダル -->
    <CalendarDetailView
      :show="view === 'calendar'"
      :district="selectedDistrict"
      :year="currentYear"
      :month="currentMonth"
      :calendar-data="calendarData"
      :has-child-modal="showEditModal || showPatternSettingsModal"
      @close="closeCalendar"
      @month-change="handleMonthChange"
      @day-click="handleDayClick"
      @open-pattern-settings="showPatternSettingsModal = true"
    />

    <!-- 収集日編集モーダル -->
    <CalendarDayEditModal
      :show="showEditModal"
      :date="editingDate"
      :current-waste-types="editingWasteTypes"
      :all-waste-types="wasteTypes"
      @close="showEditModal = false"
      @save="handleSaveDaySettings"
    />

    <!-- 基本曜日設定モーダル -->
    <CollectionPatternSettingsModal
      :show="showPatternSettingsModal"
      :district="selectedDistrict"
      :waste-types="wasteTypes"
      :initial-year="currentYear"
      @close="showPatternSettingsModal = false"
      @applied="fetchCalendarData"
    />

    <!-- トースト通知 -->
    <ToastMessage ref="toastRef" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import CollectionCalendarView from '../components/collectionCalendar/CollectionCalendarView.vue'
import CalendarDetailView from '../components/collectionCalendar/CalendarDetailView.vue'
import CalendarDayEditModal from '../components/collectionCalendar/CalendarDayEditModal.vue'
import CollectionPatternSettingsModal from '../components/collectionCalendar/CollectionPatternSettingsModal.vue'
import ToastMessage from '@/components/molecules/ToastMessage.vue'
import { getCollectionDistricts } from '@/services/systemSettings/collectionDistrictService.js'
import collectionCalendarService from '@/services/systemSettings/collectionCalendarService.js'
import { getWasteTypes } from '@/services/systemSettings/wasteTypeService.js'

const emit = defineEmits(['back-to-menu'])

const toastRef = ref(null)

const view = ref('list')
const selectedDistrict = ref(null)
const districts = ref([])
const wasteTypes = ref([])
const calendarData = ref({})
const currentYear = ref(new Date().getFullYear())
const currentMonth = ref(new Date().getMonth() + 1)
const loading = ref(false)
const error = ref(null)

const showEditModal = ref(false)
const editingDate = ref(null)
const editingWasteTypes = ref([])

const showPatternSettingsModal = ref(false)

const fetchDistricts = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await getCollectionDistricts()

    if (response.success) {
      districts.value = response.data
    } else {
      error.value = response.message || '地区データの取得に失敗しました'
    }
  } catch (err) {
    console.error('Failed to load districts:', err)
    error.value = 'データの取得中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const fetchWasteTypes = async () => {
  try {
    const response = await getWasteTypes()
    if (response.success) {
      wasteTypes.value = response.data
    }
  } catch (err) {
    console.error('Failed to load waste types:', err)
  }
}

const openCalendar = async (district) => {
  selectedDistrict.value = district
  view.value = 'calendar'
  await Promise.all([
    fetchCalendarData(),
    fetchWasteTypes()
  ])
}

const fetchCalendarData = async () => {
  if (!selectedDistrict.value) return

  loading.value = true
  error.value = null
  try {
    const data = await collectionCalendarService.getCalendarData(
      currentYear.value,
      currentMonth.value,
      selectedDistrict.value.id
    )
    calendarData.value = data.events || {}
  } catch (err) {
    console.error('カレンダーデータの取得に失敗しました:', err)
    error.value = 'カレンダーデータの取得に失敗しました'
  } finally {
    loading.value = false
  }
}

const handleMonthChange = async ({ year, month }) => {
  currentYear.value = year
  currentMonth.value = month
  await fetchCalendarData()
}

const handleDayClick = (day) => {
  if (!day.isCurrentMonth) return

  editingDate.value = new Date(day.date)
  editingWasteTypes.value = day.wasteTypes || []
  showEditModal.value = true
}

const handleSaveDaySettings = async ({ date, wasteTypes: wasteTypesParam }) => {
  loading.value = true
  try {
    const dateStr = date.toISOString().split('T')[0]
    const wasteTypeIds = wasteTypesParam.map(t => t.id)

    await collectionCalendarService.updateCalendarDay(
      dateStr,
      selectedDistrict.value.id,
      wasteTypeIds
    )

    showEditModal.value = false
    await fetchCalendarData()
    toastRef.value.success('収集日の設定を更新しました')
  } catch (err) {
    console.error('更新に失敗しました:', err)
    error.value = '更新に失敗しました'
  } finally {
    loading.value = false
  }
}

const closeCalendar = () => {
  view.value = 'list'
  selectedDistrict.value = null
  calendarData.value = {}
}

const handleBackToMenu = () => {
  emit('back-to-menu')
}

onMounted(() => {
  fetchDistricts()
})
</script>

